// ============================================
// NRS.02 D#4 — Atomic Triage Firestore Write
// ============================================
// Writes TriageData + DataCompletenessFlags atomically to
// sessions/{sessionId}/safetyProfile/current
//
// Contract:
//   - Both-or-nothing write (single setDoc = atomic)
//   - 3x retry with exponential backoff (1s/2s/4s)
//   - NRS.02 sets sexConfirmed=true, birthDateConfirmed=true
//   - Emits session_created event consumed by NRS.08
// ============================================

import { doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db, getAuthUid } from '../firebase';
import { C_SESSIONS, SC_SAFETY_PROFILE } from '../../constants/collections';
import type { TriageData } from '../../types';
import type { DataCompletenessFlags } from '../../../functions/src/nurse/types/triageSession';
import { INITIAL_DATA_COMPLETENESS_FLAGS } from '../../../functions/src/nurse/types/triageSession';

// ── Types ──

/**
 * [NRS.02 D#3 c3] Structured consent state — each consent individually recorded.
 * Not a single boolean; each field is its own value + timestamp.
 */
export interface ConsentStateRecord {
  /** Whether this consent was granted */
  granted: boolean;
  /** ISO timestamp when consent was submitted (not session start) [c4] */
  grantedAt: string | null;
  /** Whether this consent is required (cannot be withdrawn) */
  required: boolean;
}

export interface StructuredConsentState {
  // Required consents [c1]
  termsOfService: ConsentStateRecord;
  dataProcessing: ConsentStateRecord;
  // Optional consents [c2] — withdrawable via FLO.07 [c5]
  researchParticipation: ConsentStateRecord;
  marketingCommunications: ConsentStateRecord;
  clinicalDataSharing: ConsentStateRecord;
}

export interface TriageSafetyProfilePayload {
  triageData: {
    sex: TriageData['sex'];
    dateOfBirth: string;
    age: number;
    /** [c4] Only present for female patients; omitted entirely for males */
    reproductiveStatus?: string;
  };
  /** [NRS.02 D#3 c3] Structured consent object — each consent individually recorded */
  consentState: StructuredConsentState;
  dataCompletenessFlags: DataCompletenessFlags;
  createdAt: string;
  source: 'nrs02_triage';
}

export interface SessionCreatedEvent {
  eventType: 'session_created';
  sessionId: string;
  triageData: TriageSafetyProfilePayload['triageData'];
  consentState: StructuredConsentState;
  dataCompletenessFlags: DataCompletenessFlags;
  createdAt: string;
  source: 'nrs02_triage';
}

/**
 * Build a StructuredConsentState from the UI consent checkbox map.
 * [NRS.02 D#3 c3] Each consent individually recorded.
 * [NRS.02 D#3 c4] Timestamp is submission time (passed in), not session start.
 */
export function buildConsentState(
  consents: Record<string, boolean>,
  submissionTimestamp: string,
): StructuredConsentState {
  const record = (key: string, required: boolean): ConsentStateRecord => ({
    granted: !!consents[key],
    grantedAt: consents[key] ? submissionTimestamp : null,
    required,
  });

  return {
    termsOfService: record('terms_of_service', true),
    dataProcessing: record('data_processing', true),
    researchParticipation: record('research_participation', false),
    marketingCommunications: record('marketing_communications', false),
    clinicalDataSharing: record('clinical_data_sharing', false),
  };
}

// ── Constants ──

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

// ── Core: Atomic Write ──

/**
 * Atomically writes TriageData + DataCompletenessFlags to
 * sessions/{sessionId}/safetyProfile/current using a Firestore
 * WriteBatch (both-or-nothing). On success, emits a session_created
 * event to sessions/{sessionId}/events/session_created for NRS.08.
 *
 * NRS.02 owns sexConfirmed and birthDateConfirmed (set true).
 * NRS.05 owns the other 4 flags (left false).
 */
export async function atomicTriageSafetyWrite(
  sessionId: string,
  triageData: TriageData,
  consentState: StructuredConsentState,
): Promise<void> {
  // c2: Initialize all 6 flags to false, c3: then set NRS.02's two to true
  const dataCompletenessFlags: DataCompletenessFlags = {
    ...INITIAL_DATA_COMPLETENESS_FLAGS,
    sexConfirmed: true,
    birthDateConfirmed: true,
  };

  const now = new Date().toISOString();

  // [c4] Build triageData payload: omit reproductiveStatus entirely for males
  const triagePayload: TriageSafetyProfilePayload['triageData'] = {
    sex: triageData.sex,
    dateOfBirth: triageData.dateOfBirth,
    age: triageData.age,
  };
  if (triageData.sex === 'female') {
    triagePayload.reproductiveStatus = triageData.reproductiveStatus ?? 'not_applicable';
  }

  const safetyPayload: TriageSafetyProfilePayload = {
    triageData: triagePayload,
    consentState,
    dataCompletenessFlags,
    createdAt: now,
    source: 'nrs02_triage',
  };

  const sessionCreatedEvent: SessionCreatedEvent = {
    eventType: 'session_created',
    sessionId,
    triageData: triagePayload,
    consentState,
    dataCompletenessFlags,
    createdAt: now,
    source: 'nrs02_triage',
  };

  // Ensure anonymous auth is established before attempting Firestore write.
  // Without this, batch.commit() hangs indefinitely waiting for auth to resolve.
  await getAuthUid();

  // c1: Atomic batch — safetyProfile doc + session_created event in same commit
  const writeFn = async () => {
    const batch = writeBatch(db);

    const safetyRef = doc(db, C_SESSIONS, sessionId, SC_SAFETY_PROFILE, 'current');
    batch.set(safetyRef, {
      ...safetyPayload,
      _updatedAt: serverTimestamp(),
    });

    const eventRef = doc(db, C_SESSIONS, sessionId, 'events', 'session_created');
    batch.set(eventRef, {
      ...sessionCreatedEvent,
      _createdAt: serverTimestamp(),
    });

    await batch.commit();
  };

  // c4: Retry 3x with exponential backoff (1s, 2s, 4s)
  await retryWithBackoff(writeFn, MAX_RETRIES, BASE_DELAY_MS);
}

// ── Retry Logic ──

/**
 * Retries a write function up to maxRetries times with exponential backoff.
 * Delays: 1s, 2s, 4s (baseDelay * 2^(attempt-1)).
 * On exhaustion, throws an actionable error message for the patient.
 */
async function retryWithBackoff(
  writeFn: () => Promise<void>,
  maxRetries: number,
  baseDelayMs: number,
): Promise<void> {
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      await writeFn();
      return;
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      if (attempt < maxRetries) {
        const delay = baseDelayMs * Math.pow(2, attempt - 1);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw new Error(
    `Unable to save your information after ${maxRetries} attempts. ` +
    `Please check your internet connection and try again. ` +
    `If this continues, contact support@hemologix.com. ` +
    `(${lastError?.message ?? 'unknown error'})`,
  );
}
